import { UcoastEl } from '@/scripts/core/UcoastEl'
import { countdownParts, offerDeadline } from './quiz-offer-model'

export class QuizOfferCountdown extends UcoastEl {
	static htmlSelector = 'quiz-offer-countdown'
	private deadline: number | null = null
	private timer?: number
	private lastAnnouncement = ''
	private resume = () => { this.stop(); this.tick(); this.start() }
	private stop = () => { window.clearInterval(this.timer); this.timer = undefined }

	override connectedCallback() {
		super.connectedCallback()
		const deadlineType = this.dataset.ucDeadlineType || 'first_visit'
		let storage: Pick<Storage, 'getItem' | 'setItem'> | null = null
		if (deadlineType === 'first_visit') {
			if (this.dataset.ucDesignMode === 'true') {
				// Editor previews must never start or renew a visitor's actual offer.
				let preview: string | null = null
				storage = { getItem: () => preview, setItem: (_key, value) => { preview = value } }
			} else {
				try { storage = window.localStorage } catch { /* Privacy settings can deny even the getter. */ }
			}
		}
		this.deadline = this.dataset.ucEnabled === 'true' ? offerDeadline({
			deadlineType, offerKey: this.dataset.ucOfferKey || '', durationDays: Number(this.dataset.ucDurationDays), endDate: this.dataset.ucEndDate || '',
		}, storage, Date.now()) : null
		this.resume()
		document.addEventListener('visibilitychange', this.resume)
		window.addEventListener('pageshow', this.resume)
		window.addEventListener('pagehide', this.stop)
	}

	override disconnectedCallback() {
		this.stop()
		document.removeEventListener('visibilitychange', this.resume)
		window.removeEventListener('pageshow', this.resume)
		window.removeEventListener('pagehide', this.stop)
		super.disconnectedCallback()
	}

	private start() {
		if (!document.hidden && this.deadline !== null && this.deadline > Date.now()) this.timer = window.setInterval(() => this.tick(), 1000)
	}

	private tick() {
		const editor = this.dataset.ucDesignMode === 'true'
		const enabled = this.dataset.ucEnabled === 'true'
		const wrapper = this.closest<HTMLElement>('[data-uc-results-block]')!
		const warning = this.querySelector<HTMLElement>('[data-uc-quiz-editor-warning]')!
		const readout = this.querySelector<HTMLElement>('[data-uc-countdown-readout]')!
		const headline = this.querySelector<HTMLElement>('[data-uc-countdown-headline]')!
		const message = this.querySelector<HTMLElement>('[data-uc-countdown-expired]')!
		warning.hidden = !editor || this.deadline !== null
		warning.textContent = !enabled ? 'Countdown disabled. Confirm the Shopify discount, eligibility and expiry before enabling.'
			: this.dataset.ucDeadlineType === 'fixed_campaign' ? 'Countdown unavailable. Fixed campaign end requires a valid campaign end date with a timezone.'
			: 'Countdown unavailable. Select a valid deadline type. For first visit, set a campaign key and duration with browser storage available. If used, the campaign end date must be valid and include a timezone.'
		if (this.deadline === null) {
			this.dataset.ucTimerState = enabled ? 'unavailable' : 'disabled'
			wrapper.hidden = !editor
			readout.hidden = headline.hidden = message.hidden = true
			return
		}
		const remaining = this.deadline - Date.now()
		const expired = remaining <= 0
		const behavior = this.dataset.ucExpirationBehavior
		this.dataset.ucTimerState = expired ? 'expired' : 'active'
		wrapper.hidden = expired && behavior === 'hide_section' && !editor
		message.hidden = !expired || behavior !== 'show_message'
		readout.hidden = headline.hidden = expired && behavior !== 'show_zero'
		const showDays = this.dataset.ucShowDays === 'true'
		const showSeconds = this.dataset.ucShowSeconds === 'true'
		const parts = countdownParts(remaining, showDays)
		for (const unit of ['days', 'hours', 'minutes', 'seconds'] as const) {
			this.querySelector<HTMLElement>(`[data-uc-countdown-${unit}]`)!.textContent = String(parts[unit]).padStart(2, '0')
		}
		this.querySelector<HTMLElement>('[data-uc-countdown-unit="days"]')!.hidden = !showDays
		this.querySelector<HTMLElement>('[data-uc-countdown-unit="seconds"]')!.hidden = !showSeconds
		readout.style.setProperty('--qbb-units', String(2 + Number(showDays) + Number(showSeconds)))
		const summary = `${showDays ? `${parts.days} days, ` : ''}${parts.hours} hours, ${parts.minutes} minutes${showSeconds ? `, ${parts.seconds} seconds` : ''}`
		readout.setAttribute('aria-label', expired ? 'Offer expired' : `Offer expires in ${summary}`)
		// Polite status changes once a minute, not every second.
		const announcement = expired ? 'This offer has ended.' : `Offer expires in ${showDays ? `${parts.days} days, ` : ''}${parts.hours} hours, ${parts.minutes} minutes.`
		if (announcement !== this.lastAnnouncement) {
			this.querySelector<HTMLElement>('[data-uc-countdown-status]')!.textContent = announcement
			this.lastAnnouncement = announcement
		}
		if (expired) this.stop()
	}
}
